import React, { useEffect, useState } from 'react'
import {
  RefreshCw,
  Search,
  Loader2,
  LogOut,
  Home,
  Grid3x3,
  List,
  Globe,
  Star,
  Users,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAuthModal } from '@/contexts/AuthModalContext'
import { listPublicDiagrams, clonePublicDiagram } from '@/services/diagramService'
import type { DiagramListRow } from '@/types/database.types'
import { DiagramCard } from './DiagramCard'
import { AuthModal } from '@/components/Auth/AuthModal'

export const PublicDiagramsPage: React.FC = () => {
  const [diagrams, setDiagrams] = useState<DiagramListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [cloningId, setCloningId] = useState<string | null>(null)
  const { user, signOut } = useAuth()
  const { openLogin } = useAuthModal()
  const navigate = useNavigate()

  const loadDiagrams = async () => {
    setLoading(true)
    setError(null)

    const { data, error: err } = await listPublicDiagrams()

    if (err) {
      setError(err.message)
    } else {
      setDiagrams(data || [])
    }

    setLoading(false)
  }

  useEffect(() => {
    loadDiagrams()
  }, [])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (showUserMenu && !target.closest('.relative')) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  const handleOpenDiagram = async (id: string) => {
    navigate(`/diagram/${id}`)
  }

  const handleCloneDiagram = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation() // Prevent opening the diagram

    if (!user) {
      openLogin()
      return
    }

    setCloningId(id)
    const { data, error: cloneError } = await clonePublicDiagram(id)

    if (cloneError) {
      setError(cloneError.message)
      setCloningId(null)
    } else if (data) {
      setCloningId(null)
      // Navigate to the cloned diagram
      navigate(`/diagram/${data.id}`)
    }
  }

  const filteredDiagrams = diagrams.filter((d) =>
    d.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const templates = filteredDiagrams.filter((d) => d.is_template)
  const communityDiagrams = filteredDiagrams.filter((d) => !d.is_template)

  const handleSignOut = async () => {
    await signOut()
    setShowUserMenu(false)
    navigate('/')
  }

  const getUserInitials = () => {
    if (!user?.email) return 'U'
    return user.email.charAt(0).toUpperCase()
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-600 dark:text-blue-400" />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Loading public diagrams...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2">
                <img src="/logo.svg" alt="Degre120" className="h-8 w-auto" />
              </div>

              {/* Navigation Links */}
              <nav className="hidden md:flex md:space-x-6">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  <Home className="h-4 w-4" />
                  Home
                </button>
                <button
                  onClick={() => navigate('/editor')}
                  className="text-sm font-medium text-gray-600 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  Editor
                </button>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  Explore
                </span>
                {user && (
                  <button
                    onClick={() => navigate('/my-diagrams')}
                    className="text-sm font-medium text-gray-600 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    My Diagrams
                  </button>
                )}
              </nav>
            </div>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 transition hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-semibold text-white shadow-sm">
                    {getUserInitials()}
                  </div>
                  <span className="hidden text-sm font-medium text-gray-700 dark:text-gray-300 md:inline">
                    {user?.email?.split('@')[0] || 'User'}
                  </span>
                </button>

                {/* User Menu Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {user?.email}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Personal account
                      </p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <LogOut className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => openLogin()}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Page Header */}
      <div className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Public Diagrams
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {diagrams.length} public diagram{diagrams.length !== 1 ? 's' : ''} shared by the community
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* View Toggle */}
              <div className="inline-flex rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`inline-flex items-center gap-1.5 rounded-l-lg px-3 py-2 text-sm font-medium transition ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                  title="Grid view"
                >
                  <Grid3x3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`inline-flex items-center gap-1.5 rounded-r-lg px-3 py-2 text-sm font-medium transition ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                  title="List view"
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>

              <button
                onClick={loadDiagrams}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search public diagrams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {filteredDiagrams.length === 0 ? (
          <div className="py-12 text-center">
            <Globe className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              {searchQuery ? 'No diagrams found' : 'No public diagrams yet'}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {searchQuery
                ? 'Try a different search term'
                : 'Be the first to share a diagram with the community!'}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Official Templates Section */}
            {templates.length > 0 && (
              <section>
                <div className="mb-6 flex items-center gap-3">
                  <Star className="h-6 w-6 text-yellow-500" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Official Templates
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {templates.length} professionally designed template{templates.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className={viewMode === 'grid' ? 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3' : 'space-y-4'}>
                  {templates.map((diagram) => (
                    <DiagramCard
                      key={diagram.id}
                      diagram={diagram}
                      onOpen={handleOpenDiagram}
                      onClone={handleCloneDiagram}
                      isCloning={cloningId === diagram.id}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Community Diagrams Section */}
            {communityDiagrams.length > 0 && (
              <section>
                <div className="mb-6 flex items-center gap-3">
                  <Users className="h-6 w-6 text-blue-500" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Community Diagrams
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {communityDiagrams.length} diagram{communityDiagrams.length !== 1 ? 's' : ''} shared by users
                    </p>
                  </div>
                </div>
                <div className={viewMode === 'grid' ? 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3' : 'space-y-4'}>
                  {communityDiagrams.map((diagram) => (
                    <DiagramCard
                      key={diagram.id}
                      diagram={diagram}
                      onOpen={handleOpenDiagram}
                      onClone={handleCloneDiagram}
                      isCloning={cloningId === diagram.id}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      <AuthModal />
    </div>
  )
}
